import userQueries from '@apis/queries/userQueries';
import UserApi from '@apis/user/UserApi';
import useToast from '@hooks/ui/useToast';
import { useFlow } from '@stackflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { containsProfanity } from '@utils/profanityUtils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useUserDetail from './useUserDetail';

function useNickname() {
    const { t } = useTranslation('accountSetting');
    const [nickname, setNickname] = useState<string>('');
    const toast = useToast();
    const userDetail = useUserDetail();
    const nicknameHelperText = nickname.length > 16
        ? t('helper.nicknameMaxLength16') : '';
    const nicknameErrorText = containsProfanity(nickname)
        ? t('helper.nicknameProfanity') : '';
    const { push } = useFlow();
    const queryClient = useQueryClient();
    const { mutate } = useMutation({
        mutationFn: UserApi.updateNickname,
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: userQueries._def });
            push('NickNameChangeModal', {});
        },
        onError(e) {
            console.error(e);
            toast.error(t('toast.nicknameError'));
        },
    });
    const handleUpdateNickname = async () => {
        if (nicknameHelperText.length > 20) {
            toast.error(t('toast.nicknameMaxLength'));
            return;
        }
        if (nickname.length === 0) {
            toast.error(t('toast.nicknameRequired'));
            return;
        }
        // 금칙어 오류는 TextField 에 이미 표시되어 있으므로 요청만 막는다
        if (nicknameErrorText) {
            return;
        }
        mutate(nickname);
    };

    useEffect(() => {
        if (userDetail) {
            setNickname(userDetail.nickname);
        }
    }, [userDetail]);
    return {
        nickname,
        setNickname,
        handleUpdateNickname,
        nicknameHelperText,
        nicknameErrorText,
    };
}

export default useNickname;
